import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, UserCheck } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface UserTypeSelectionModalProps {
  userEmail: string;
  userName: string;
  onComplete: (userType: 'student' | 'teacher', classGrade?: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function UserTypeSelectionModal({ 
  userEmail, 
  userName, 
  onComplete, 
  isLoading = false, 
  error 
}: UserTypeSelectionModalProps) {
  const [userType, setUserType] = useState<'student' | 'teacher' | undefined>(undefined);
  const [classGrade, setClassGrade] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setValidationError(null);

    // Validate user type selection
    if (!userType) {
      setValidationError('Please select whether you are a student or teacher');
      return;
    }

    // Validate class/grade for students
    if (userType === 'student' && !classGrade) {
      setValidationError('Please select your class/grade');
      return;
    }

    try {
      await onComplete(userType, userType === 'student' ? classGrade : undefined);
    } catch (error) {
      console.error('Error completing user type selection:', error);
    }
  };

  const handleUserTypeChange = (value: 'student' | 'teacher') => {
    setUserType(value);
    // Clear class/grade when switching to teacher
    if (value === 'teacher') {
      setClassGrade('');
    }
    // Clear validation error when user makes a selection
    if (validationError) {
      setValidationError(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <UserCheck className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle>Complete Your Profile</CardTitle>
          <CardDescription>
            Welcome, {userName}! Please tell us about yourself to complete your sign-up.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {validationError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="userType">I am a...</Label>
            <Select
              value={userType}
              onValueChange={handleUserTypeChange}
              disabled={isLoading}
            >
              <SelectTrigger 
                className={`h-10 ${validationError && !userType ? 'border-red-500 focus:border-red-500' : ''}`}
              >
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="teacher">Teacher</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {userType === 'student' && (
            <div className="space-y-2">
              <Label htmlFor="classGrade">Class/Grade</Label>
              <Select
                value={classGrade}
                onValueChange={(value) => {
                  setClassGrade(value);
                  // Clear validation error when user makes a selection
                  if (validationError) {
                    setValidationError(null);
                  }
                }}
                disabled={isLoading}
              >
                <SelectTrigger 
                  className={`h-10 ${validationError && !classGrade ? 'border-red-500 focus:border-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select your class/grade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="9">Grade 9</SelectItem>
                  <SelectItem value="10">Grade 10</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="pt-4">
            <Button 
              onClick={handleSubmit} 
              className="w-full" 
              disabled={isLoading}
            >
              {isLoading ? 'Creating Profile...' : 'Complete Sign Up'}
            </Button>
          </div>

          <div className="text-xs text-muted-foreground text-center">
            Account: {userEmail}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
